<!--
📌 Componente: QRCodeLeserScann.vue
📂 Caminho: src/components/QRCodeLeserScann.vue
📝 Descrição: Scanner dedicado à conferência de inventário. Controla o
  ciclo do Html5Qrcode (Web) ou usa scan() do ML Kit (Android nativo),
  emite o texto decodificado e permite reiniciar leituras
  automaticamente após cada confirmação no componente pai.

👁️ Interface e Interação com o Usuário:
  - Área fixa para renderizar o vídeo do Html5Qrcode (Web).
  - No Android, abre a UI nativa do Google ML Kit.
  - Slot opcional para sobreposição de feedback/indicadores.
  - Scanner inicia/paralisa conforme a prop `active` ou via métodos
    expostos (`start/stop/restart`).

📥 Entrada (Props):
  - `active` (Boolean) - Ativa/desativa o scanner automaticamente.
  - `fps` (Number) - Frames por segundo desejados (padrão 20).
  - `qrbox` (Object) - Dimensão da área de leitura ({ width, height }).
  - `pauseAfterScan` (Boolean) - Se true, pausa após decodificar.
  - `beep` (Boolean) - Habilita/desabilita o feedback sonoro.
  - `beepSuccess` (Boolean) - Som de sucesso ao decodificar.
  - `beepError` (Boolean) - Som de alerta em falhas/erros.
  - `beepVolume` (Number) - Volume base (0.0–0.1, padrão 0.1).
  - `beepSuccessFreq` (Number) - Frequência Hz do sucesso (padrão 1200).
  - `beepErrorFreq` (Number) - Frequência Hz do erro (padrão 400).
  - `beepDurMs` (Number) - Duração do beep em ms (padrão 120).
  - `beepErrorThrottleMs` (Number) - Intervalo mínimo entre erros.
  - `beepErrorVolume` (Number) - Volume específico para erros.

📤 Saída (Emits):
  - `decoded` - Emite o texto bruto lido do QR Code.
  - `error` - Emite erros relevantes do scanner.
  - `started` - Dispara ao iniciar captura.
  - `stopped` - Dispara ao parar captura.

🔗 Dependências:
  - **Bibliotecas externas:** html5-qrcode (Html5Qrcode), @capacitor-mlkit/barcode-scanning
-->
<template>
  <div class="w-full flex justify-center">
    <!-- Container para Web (html5-qrcode) -->
    <div
      v-if="!nativeMode"
      :id="containerId"
      class="rounded border border-cinza-200 bg-cinza-900/5"
      :style="boxStyle"
    />
    <slot />
  </div>
</template>

<script>
import { ref, watch, onBeforeUnmount, nextTick, computed } from "vue";
import { Html5Qrcode } from "html5-qrcode";
import { Capacitor } from "@capacitor/core";
import { BarcodeScanner } from "@capacitor-mlkit/barcode-scanning";

export default {
  name: "QRCodeLeserScann",
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    fps: {
      type: Number,
      default: 20,
    },
    qrbox: {
      type: Object,
      default: () => ({
        width: 250,
        height: 250,
      }),
    },
    pauseAfterScan: {
      type: Boolean,
      default: true,
    },
    beep: {
      type: Boolean,
      default: true,
    },
    beepSuccess: {
      type: Boolean,
      default: true,
    },
    beepError: {
      type: Boolean,
      default: true,
    },
    beepVolume: {
      type: Number,
      default: 0.1,
    },
    beepSuccessFreq: {
      type: Number,
      default: 1200,
    },
    beepErrorFreq: {
      type: Number,
      default: 400,
    },
    beepErrorVolume: {
      type: Number,
      default: 0.1,
    },
    beepDurMs: {
      type: Number,
      default: 120,
    },
    beepErrorThrottleMs: {
      type: Number,
      default: 500,
    },
  },
  emits: ["decoded", "error", "started", "stopped"],
  setup(props, { emit, expose }) {
    const containerId = `qr-inventur-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    const html5Instance = ref(null);
    const isStarting = ref(false);
    const audioCtx = ref(null);
    const lastErrorBeepAt = ref(0);
    const nativeMode = ref(Capacitor.isNativePlatform());

    const boxStyle = computed(() => {
      const w = props.qrbox?.width ?? 250;
      const h = props.qrbox?.height ?? 250;
      return {
        width: `${w}px`,
        height: `${h}px`,
      };
    });

    const clampVolume = (value) => {
      const max = 0.1;
      const min = 0;
      const vol = Number.isFinite(value) ? value : 0.02;
      return Math.min(Math.max(vol, min), max);
    };

    const ensureAudioCtx = async () => {
      if (!props.beep) return null;
      if (typeof window === "undefined") return null;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      if (!audioCtx.value) {
        try {
          audioCtx.value = new AC();
        } catch (error) {
          console.warn("[QRCodeLeserScann] Audio init failed:", error);
          audioCtx.value = null;
          return null;
        }
      }
      if (audioCtx.value.state === "suspended") {
        try {
          await audioCtx.value.resume();
        } catch (error) {
          console.warn("[QRCodeLeserScann] Audio resume failed:", error);
        }
      }
      return audioCtx.value;
    };

    const playBeep = async (freq, durMs, volume) => {
      if (!props.beep) return;
      const ctx = await ensureAudioCtx();
      if (!ctx) return;
      const gainValue = clampVolume(volume);
      if (gainValue <= 0) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(gainValue, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + durMs / 1000);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + durMs / 1000);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
      };
    };

    const playSuccess = () => {
      if (!props.beepSuccess) return;
      playBeep(props.beepSuccessFreq, props.beepDurMs, props.beepVolume);
    };

    const playError = (useThrottle = true) => {
      if (!props.beepError) return;
      const now = performance.now();
      if (
        useThrottle &&
        now - lastErrorBeepAt.value < props.beepErrorThrottleMs
      ) {
        return;
      }
      lastErrorBeepAt.value = now;
      const volume =
        props.beepErrorVolume !== null
          ? clampVolume(props.beepErrorVolume)
          : clampVolume(props.beepVolume * 1.5);
      playBeep(props.beepErrorFreq, props.beepDurMs, volume);
    };

    // --- LÓGICA NATIVA (ML Kit - método scan()) ---
    const startNativeScan = async () => {
      try {
        const moduleCheck =
          await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
        if (!moduleCheck.available) {
          await BarcodeScanner.installGoogleBarcodeScannerModule();
          emit("error", "Google Barcode Scanner module not available");
          return;
        }

        emit("started");

        const { barcodes } = await BarcodeScanner.scan();

        if (barcodes && barcodes.length > 0) {
          const rawValue = barcodes[0].rawValue || barcodes[0].displayValue;
          playSuccess();
          emit("decoded", rawValue);
        }

        emit("stopped");
      } catch (error) {
        const msg = error?.message || String(error);
        if (!/cancel/i.test(msg)) {
          console.error("[QRCodeLeserScann] Native scan error:", error);
          playError(false);
          emit("error", error);
        }
        emit("stopped");
      }
    };

    // --- LÓGICA WEB (html5-qrcode) ---
    const start = async () => {
      if (isStarting.value) return;
      if (!props.active) return;

      // No Android, usar scan() nativo
      if (nativeMode.value) {
        return startNativeScan();
      }

      if (html5Instance.value) return;

      isStarting.value = true;
      try {
        await nextTick();
        const instance = new Html5Qrcode(containerId);
        html5Instance.value = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: props.fps, qrbox: props.qrbox },
          handleDecode,
          handleScanError
        );
        emit("started");
      } catch (error) {
        playError(false);
        emit("error", error);
        await stopInternal({ emitStopped: false, suppressError: true });
      } finally {
        isStarting.value = false;
      }
    };

    const stopInternal = async (options = {}) => {
      const { emitStopped = true, suppressError = false } = options;
      const instance = html5Instance.value;
      if (!instance) return;
      html5Instance.value = null;
      try {
        await instance.stop();
      } catch (error) {
        if (!suppressError) playError(false);
        if (!suppressError) emit("error", error);
      }
      try {
        await instance.clear();
      } catch (error) {
        const message = error?.message || "";
        if (!/close it first/i.test(message) && !suppressError) {
          playError(false);
          emit("error", error);
        }
      }
      if (emitStopped) {
        emit("stopped");
      }
    };

    const stop = async () => {
      await stopInternal();
    };

    const restart = async () => {
      if (!props.active || isStarting.value) return;
      await stopInternal();
      await start();
    };

    const handleDecode = async (decodedText) => {
      playSuccess();
      if (props.pauseAfterScan) {
        await stopInternal();
      }
      emit("decoded", decodedText);
    };

    const handleScanError = (error) => {
      console.warn("[QRCodeLeserScann] Scan issue:", error);
    };

    watch(
      () => props.active,
      (val) => {
        if (val) {
          start();
        } else {
          stop();
        }
      },
      { immediate: true }
    );

    onBeforeUnmount(() => {
      stopInternal({
        emitStopped: false,
        suppressError: true,
      });
      if (audioCtx.value) {
        audioCtx.value.close().catch(() => {});
        audioCtx.value = null;
      }
    });

    expose({
      start,
      stop,
      restart,
      isActive: () => Boolean(html5Instance.value),
    });

    return {
      containerId,
      boxStyle,
      nativeMode,
    };
  },
};
</script>
